import {
  filterGoals,
  getGoalListOrder,
  getLifeAreaGoalFilterDocument,
} from '@griply/utils';
import { useSortedGoals } from 'features/goals';
import { useUpdateLifeArea } from 'features/life-areas';
import { useUpdateUserSettings } from 'features/user';
import React, { useCallback, useMemo, useRef } from 'react';
import { useOpenCreateGoalDialog } from 'shared/contexts/goal-form';
import { useTaskCounts } from 'shared/contexts/task-counts/use-task-counts';
import { useUser } from 'shared/hooks/use-user';
import { LifeArea } from 'shared/types/life-area';
import { GoalSortingMode, OrderDirection } from 'shared/types/sorting-mode';
import {
  GoalFilterList,
  SortBar,
  useGoalsByFilterDocument,
} from 'shared/views/goal-filter-list';
import { ContextMenuOptions } from 'shared/views/goal-filter-list/types';

import { useGoalTreeReorder } from '../../hooks/use-goal-tree-reorder';
import { LifeAreaGoalsEmptyState } from '../life-area-goals-empty-state';
import * as Styled from './goals-overview.style';

const excludedContextMenuOptions = [ContextMenuOptions.ShowRoadmap];

type GoalsOverviewProps = {
  lifeArea: LifeArea;
  scrollElement?: React.RefObject<HTMLDivElement>;
  hideCompleted?: boolean;
  hideArchived?: boolean;
  fullSize?: boolean;
};

export const GoalsOverview: React.FC<GoalsOverviewProps> = ({
  lifeArea,
  scrollElement,
  hideCompleted,
  hideArchived,
  fullSize,
}) => {
  const { submit: updateLifeArea } = useUpdateLifeArea();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const openCreateGoalDialog = useOpenCreateGoalDialog();
  const allGoals = useSortedGoals();
  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();
  const taskCounts = useTaskCounts();

  // The SortBar is the goal table's column header (each column label doubles as
  // a quick-sort toggle); it writes the same per-area `goalSortingMode` as the
  // LifeAreaGoalsFilterButton, so the two stay in sync.
  const sortMode = lifeArea.goalSortingMode ?? GoalSortingMode.Custom;
  const sortDirection = user?.settings?.goalSortDirection ?? OrderDirection.Asc;

  const onSortChange = useCallback(
    (mode: GoalSortingMode) => {
      updateLifeArea({ id: lifeArea.id, goalSortingMode: mode });
      updateUserSettings({ goalSortDirection: OrderDirection.Asc });
    },
    [lifeArea.id, updateLifeArea, updateUserSettings],
  );

  const onSortDirectionToggle = useCallback(() => {
    updateUserSettings({
      goalSortDirection:
        sortDirection === OrderDirection.Asc
          ? OrderDirection.Desc
          : OrderDirection.Asc,
    });
  }, [sortDirection, updateUserSettings]);

  const onSectionCollapse = useCallback(
    (newCollapsedSections: string[]) => {
      updateLifeArea({
        id: lifeArea.id,
        collapsedGoals: newCollapsedSections,
      });
    },
    [lifeArea.id, updateLifeArea],
  );

  const filterDocument = useMemo(
    () => getLifeAreaGoalFilterDocument(lifeArea, allGoals),
    [allGoals, lifeArea],
  );

  const filteredGoals = useGoalsByFilterDocument(filterDocument, {
    customOrder: undefined,
  });

  const customOrder = useMemo(
    () =>
      getGoalListOrder({
        goals: filteredGoals,
        allGoals,
        sortMode,
        sortDirection,
        goalSorting: lifeArea.goalSorting,
        taskCounts: taskCounts?.byGoalId,
        goalProgressCounts: taskCounts?.byGoalProgress,
      }),
    [
      filteredGoals,
      allGoals,
      sortMode,
      sortDirection,
      lifeArea.goalSorting,
      taskCounts?.byGoalId,
      taskCounts?.byGoalProgress,
    ],
  );

  const onAddGoal = () => {
    openCreateGoalDialog({ lifeAreaId: lifeArea.id });
  };

  const onReorder = useGoalTreeReorder(lifeArea.id);

  if (!filterGoals(filteredGoals, { hideArchived, hideCompleted }).length) {
    return <LifeAreaGoalsEmptyState onAdd={onAddGoal} />;
  }

  return (
    <Styled.ScrollContainer ref={scrollContainerRef} $scrollAuto={!!fullSize}>
      <SortBar
        value={sortMode}
        direction={sortDirection}
        onChange={onSortChange}
        onDirectionToggle={onSortDirectionToggle}
        hideLifeArea
      />
      <GoalFilterList
        filterDocument={filterDocument}
        customOrder={customOrder}
        onSectionCollapse={onSectionCollapse}
        scrollElement={
          fullSize ? scrollContainerRef : scrollElement ?? scrollContainerRef
        }
        hideCompleted={hideCompleted}
        hideArchived={hideArchived}
        onReorder={onReorder}
        excludedContextMenuOptions={excludedContextMenuOptions}
        hideLifeArea
      />
    </Styled.ScrollContainer>
  );
};
